import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { ApiError, apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  ProductionCostReportPeriodPicker,
  type ProductionCostReportPeriod,
} from "@/pages/manufacturing/ProductionCostReportPeriodPicker";

interface ClampQcRateOperatorRow {
  userId: string;
  displayName: string;
  days: number;
  units: number;
  hours: number;
  rate: number;
}

interface ClampQcRateDayRow {
  date: string;
  averageRate: number;
  rates: Record<string, number>;
}

interface ClampQcRateReport {
  timeZone: string;
  startDate: string;
  endDate: string;
  minRatePerHour: number;
  operators: ClampQcRateOperatorRow[];
  days: ClampQcRateDayRow[];
}

const OPERATOR_LINE_CLASSES = [
  "text-fuchsia-600",
  "text-violet-600",
  "text-teal-600",
  "text-amber-600",
  "text-sky-600",
  "text-rose-600",
] as const;

const CHART_WIDTH = 860;
const CHART_HEIGHT = 360;
const PAD = { top: 16, right: 16, bottom: 36, left: 44 };
const Y_MIN = 0;
const Y_MAX = 70;
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

type OperatorSortKey = "displayName" | "days" | "units" | "hours" | "rate";

const OPERATOR_TOTAL_COLUMNS: HubDataTableColumn<ClampQcRateOperatorRow, OperatorSortKey>[] = [
  {
    id: "displayName",
    header: "Operator",
    sortKey: "displayName",
    sortValue: (row) => row.displayName,
    cellClassName: "font-medium text-slate-900",
    cell: (row) => row.displayName,
  },
  {
    id: "days",
    header: "Days",
    sortKey: "days",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.days,
    cell: (row) => row.days,
  },
  {
    id: "units",
    header: "Clamps",
    sortKey: "units",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.units,
    cell: (row) => row.units.toLocaleString("en-US"),
  },
  {
    id: "hours",
    header: "Hours",
    sortKey: "hours",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.hours,
    cell: (row) => row.hours.toFixed(1),
  },
  {
    id: "rate",
    header: "Checked / hour",
    sortKey: "rate",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.rate,
    cellClassName: "text-slate-900",
    cell: (row) => row.rate.toFixed(1),
  },
];

function chicagoDateToday(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  return `${year}-${month}-${day}`;
}

function yearToDatePeriod(): ProductionCostReportPeriod {
  const endDate = chicagoDateToday();
  return { startDate: `${endDate.slice(0, 4)}-01-01`, endDate };
}

function previousFourWeeks(): ProductionCostReportPeriod {
  const today = chicagoDateToday();
  const anchor = new Date(`${today}T12:00:00`);
  const weekday = anchor.getDay();
  const daysFromMonday = (weekday + 6) % 7;
  const thisMonday = new Date(anchor);
  thisMonday.setDate(anchor.getDate() - daysFromMonday);
  const lastSunday = new Date(thisMonday);
  lastSunday.setDate(thisMonday.getDate() - 1);
  const firstMonday = new Date(lastSunday);
  firstMonday.setDate(lastSunday.getDate() - 27);
  const format = (value: Date) =>
    `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  return { startDate: format(firstMonday), endDate: format(lastSunday) };
}

function axisLabel(days: ClampQcRateDayRow[], index: number): string {
  const date = days[index]?.date;
  if (date == null) {
    return "";
  }
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));
  if (index === 0) {
    return `${MONTH_NAMES[month - 1]} ${String(day)}`;
  }
  const previousMonth = Number(days[index - 1]?.date.slice(5, 7));
  if (month !== previousMonth) {
    return `${MONTH_NAMES[month - 1]} ${String(day)}`;
  }
  return "";
}

function polylinePath(
  points: Array<{ index: number; value: number }>,
  xAt: (index: number) => number,
  yAt: (value: number) => number,
): string {
  return points
    .map((point, offset) => {
      const command = offset === 0 ? "M" : "L";
      return `${command}${xAt(point.index).toFixed(1)} ${yAt(point.value).toFixed(1)}`;
    })
    .join(" ");
}

/** Split when an operator skipped a day that is still on the axis (someone else worked). */
function polylineSegments(
  points: Array<{ index: number; value: number }>,
): Array<Array<{ index: number; value: number }>> {
  const segments: Array<Array<{ index: number; value: number }>> = [];
  let current: Array<{ index: number; value: number }> = [];
  for (const point of points) {
    const previous = current[current.length - 1];
    if (previous != null && point.index > previous.index + 1) {
      segments.push(current);
      current = [point];
    } else {
      current.push(point);
    }
  }
  if (current.length > 0) {
    segments.push(current);
  }
  return segments;
}

function ClampQcRateChart({ report }: { report: ClampQcRateReport }) {
  const days = report.days;
  const lastIndex = Math.max(days.length - 1, 1);
  const plotWidth = CHART_WIDTH - PAD.left - PAD.right;
  const plotHeight = CHART_HEIGHT - PAD.top - PAD.bottom;
  const xAt = (index: number) => PAD.left + (index / lastIndex) * plotWidth;
  const yAt = (value: number) => {
    const clamped = Math.min(Y_MAX, Math.max(Y_MIN, value));
    return PAD.top + (1 - (clamped - Y_MIN) / (Y_MAX - Y_MIN)) * plotHeight;
  };
  const yTicks = [0, 20, 40, 60];

  const series = [
    {
      key: "average",
      name: "Station average",
      className: "text-slate-800",
      strokeWidth: 2.5,
      points: days.map((row, index) => ({ index, value: row.averageRate })),
    },
    ...report.operators.map((operator, operatorIndex) => ({
      key: operator.userId,
      name: operator.displayName,
      className: OPERATOR_LINE_CLASSES[operatorIndex % OPERATOR_LINE_CLASSES.length],
      strokeWidth: 2,
      points: days.flatMap((row, index) => {
        const value = row.rates[operator.userId];
        return value == null ? [] : [{ index, value }];
      }),
    })),
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-4 text-xs text-slate-600">
        {series.map((entry) => (
          <span key={entry.key} className="inline-flex items-center gap-1.5">
            <span className={cn("h-2.5 w-2.5 rounded-full", entry.className.replace("text-", "bg-"))} />
            {entry.name}
          </span>
        ))}
      </div>
      <svg
        viewBox={`0 0 ${String(CHART_WIDTH)} ${String(CHART_HEIGHT)}`}
        width="100%"
        height={CHART_HEIGHT}
        role="img"
        aria-label="Clamp QC clamps checked per labor hour by operator"
      >
        {yTicks.map((tick) => {
          const y = yAt(tick);
          return (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={CHART_WIDTH - PAD.right}
                y1={y}
                y2={y}
                className="stroke-slate-200"
                strokeWidth={1}
              />
              <text x={PAD.left - 8} y={y + 4} textAnchor="end" className="fill-slate-500" fontSize={11}>
                {tick}
              </text>
            </g>
          );
        })}
        <line
          x1={PAD.left}
          x2={PAD.left}
          y1={PAD.top}
          y2={CHART_HEIGHT - PAD.bottom}
          className="stroke-slate-300"
          strokeWidth={1}
        />
        <line
          x1={PAD.left}
          x2={CHART_WIDTH - PAD.right}
          y1={CHART_HEIGHT - PAD.bottom}
          y2={CHART_HEIGHT - PAD.bottom}
          className="stroke-slate-300"
          strokeWidth={1}
        />
        {days.map((row, index) => {
          const label = axisLabel(days, index);
          if (label === "") {
            return null;
          }
          return (
            <text
              key={row.date}
              x={xAt(index)}
              y={CHART_HEIGHT - 12}
              textAnchor="middle"
              className="fill-slate-500"
              fontSize={11}
            >
              {label}
            </text>
          );
        })}
        {series.map((entry) => {
          if (entry.points.length === 0) {
            return null;
          }
          return (
            <g key={entry.key} className={entry.className}>
              {polylineSegments(entry.points).map((segment, segmentIndex) =>
                segment.length > 1 ? (
                  <path
                    key={`${entry.key}-line-${String(segmentIndex)}`}
                    d={polylinePath(segment, xAt, yAt)}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={entry.strokeWidth}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                ) : null,
              )}
              {entry.points.map((point) => (
                <circle
                  key={`${entry.key}-${String(point.index)}`}
                  cx={xAt(point.index)}
                  cy={yAt(point.value)}
                  r={2.2}
                  fill="currentColor"
                />
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function ClampQcReportTab({ refreshGeneration = 0 }: { refreshGeneration?: number }) {
  const defaultWindow = useMemo(() => yearToDatePeriod(), []);
  const [draft, setDraft] = useState<ProductionCostReportPeriod>(defaultWindow);
  const [applied, setApplied] = useState<ProductionCostReportPeriod>(defaultWindow);
  const [sortKey, setSortKey] = useState<OperatorSortKey>("rate");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const query = useQuery<ClampQcRateReport, ApiError>({
    queryKey: ["manufacturing", "reporting", "clamp-qc-rate", applied.startDate, applied.endDate, refreshGeneration],
    queryFn: () =>
      apiFetch<ClampQcRateReport>("/api/manufacturing/reporting/clamp-qc-rate", {
        query: { startDate: applied.startDate, endDate: applied.endDate },
      }),
    staleTime: 60_000,
  });

  const report = query.data;
  function onSort(nextKey: OperatorSortKey): void {
    if (nextKey === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDir(nextKey === "displayName" ? "asc" : "desc");
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-900">Clamp QC rate</h2>
        <ProductionCostReportPeriodPicker
          value={draft}
          onChange={(next) => {
            setDraft(next);
            setApplied(next);
          }}
          previousFourWeeks={previousFourWeeks}
        />
      </div>

      {query.isLoading ? (
        <p className="py-6 text-center text-sm text-slate-500">Loading Clamp QC rate...</p>
      ) : query.error ? (
        <p className="py-6 text-center text-sm text-rose-700">{query.error.message}</p>
      ) : report == null || report.days.length === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-600">
          No Clamp QC production days at {report?.minRatePerHour ?? 15} or more clamps per hour were found for this
          window.
        </p>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {report.operators.slice(0, 4).map((operator) => (
              <Card key={operator.userId}>
                <CardBody className="flex flex-col gap-1 px-3 py-4">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {operator.displayName}
                  </span>
                  <span className="text-xl font-semibold tabular-nums text-slate-900">
                    {operator.rate.toFixed(1)} /hr
                  </span>
                </CardBody>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitleWithHelp hoverHint="Each point is clamps checked per productive labor hour. Days with no Clamp QC, and days under 15 clamps per hour (clocked in with almost no scans), are omitted so lines do not dive to the axis. An operator line is drawn only through days that person worked.">
                Daily clamps per hour
              </CardTitleWithHelp>
            </CardHeader>
            <CardBody>
              <ClampQcRateChart report={report} />
              <p className="mt-2 text-xs text-slate-500">
                {report.days.length} production days in {report.startDate} through {report.endDate} (
                {report.timeZone}). Idle days below {String(report.minRatePerHour)} clamps per hour are omitted.
              </p>
            </CardBody>
          </Card>

          <HubDataTable
            title={
              <CardTitleWithHelp hoverHint="Totals include every Clamp QC clocked hour in the window, including slow days. Operators with less than one hour are omitted.">
                Operator totals
              </CardTitleWithHelp>
            }
            columns={OPERATOR_TOTAL_COLUMNS}
            rows={report.operators}
            rowKey={(row) => row.userId}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
            empty="No operator totals for this window."
          />
        </>
      )}
    </div>
  );
}
